/**
 * FolderState - Reactive state management for a single folder
 * 
 * Manages folder metadata and upload progress.
 * Photo data is now managed by PhotoService.
 * 
 * Events emitted:
 * - 'folderUpdated' - Folder metadata changed (detail: { folder })
 * - 'uploadProgress' - Upload progress (detail: { uploaded, total, percentage })
 */

export class FolderState extends EventTarget {
  constructor(folder, photoService = null) {
    super();
    
    this._state = {
      folder: folder,           // Folder metadata { id, name, folder_type, photos_count }
      uploadProgress: null      // { total, uploaded } or null
    };
    
    // Reference to shared PhotoService
    this.photoService = photoService;
    
    // Listen to PhotoService events for this folder's photos
    if (this.photoService) {
      this.setupPhotoServiceListeners();
    }
  }
  
  /**
   * Setup listeners for PhotoService updates
   */
  setupPhotoServiceListeners() {
    // Forward photoUpdated events for photos in this folder
    this.photoService.addEventListener('photoUpdated', (e) => {
      const { photo } = e.detail;
      if (photo.folder_id === this.getId()) {
        // Re-emit for folder-specific listeners
        this.dispatchEvent(new CustomEvent('photoUpdated', {
          detail: e.detail
        }));
      }
    });
    
    // Forward photosLoaded events for this folder
    this.photoService.addEventListener('photosLoaded', (e) => {
      const { folderId } = e.detail;
      if (folderId === this.getId()) {
        this.dispatchEvent(new CustomEvent('photoMetadataLoaded', {
          detail: e.detail
        }));
      }
    });
    
    // Forward pollingComplete events for this folder
    this.photoService.addEventListener('pollingComplete', (e) => {
      const { folderId } = e.detail;
      if (folderId === this.getId()) {
        this.dispatchEvent(new CustomEvent('pollingComplete', {
          detail: e.detail
        }));
      }
    });
  }
  
  /**
   * Get folder metadata
   */
  getFolder() {
    return this._state.folder;
  }
  
  /**
   * Get folder ID
   */
  getId() {
    return this._state.folder.id;
  }
  
  /**
   * Get folder name
   */
  getName() {
    return this._state.folder.name;
  }
  
  /**
   * Update folder metadata
   */
  updateFolder(updates) {
    this._state.folder = { ...this._state.folder, ...updates };
    
    this.dispatchEvent(new CustomEvent('folderUpdated', {
      detail: { folder: this._state.folder }
    }));
  }
  
  /**
   * Get basic photo metadata (delegates to PhotoService)
   */
  getPhotoMetadata() {
    if (!this.photoService) return [];
    return this.photoService.getPhotosForFolder(this.getId());
  }
  
  /**
   * Set basic photo metadata (loads into PhotoService)
   */
  setPhotoMetadata(photoMetadata) {
    // Load metadata into PhotoService
    if (this.photoService) {
      photoMetadata.forEach(photo => {
        this.photoService.addPhoto(photo, false); // hasFullData = false
      });
    }
    
    // Update folder count
    if (photoMetadata.length > 0 && this._state.folder.photos_count !== photoMetadata.length) {
      this._state.folder.photos_count = photoMetadata.length;
      this.dispatchEvent(new CustomEvent('folderUpdated', {
        detail: { folder: this._state.folder }
      }));
    }
    
    // Start polling if needed
    this.startPollingIfNeeded();
  }
  
  /**
   * Check if metadata has been loaded
   */
  hasMetadata() {
    if (!this.photoService) return false;
    // Check if the folder's basic info has been fully loaded
    return this.photoService.hasFolderBasicInfo(this.getId());
  }
  
  /**
   * Get photo count
   */
  getPhotoCount() {
    return this._state.folder.photos_count || 0;
  }
  
  /**
   * Get full photo data for specific photos (from cache)
   */
  getPhotos(photoIds = null) {
    if (!this.photoService) return [];
    
    if (photoIds) {
      return photoIds.map(id => this.photoService.getPhotoById(id)).filter(Boolean);
    }
    
    // Return all photos for this folder
    return this.photoService.getPhotosForFolder(this.getId());
  }
  
  /**
   * For backwards compatibility
   */
  setPhotos(photos) {
    console.warn('FolderState.setPhotos() is deprecated, use setPhotoMetadata()');
    this.setPhotoMetadata(photos);
  }
  
  /**
   * For backwards compatibility
   */
  hasFullPhotos() {
    return this.hasMetadata();
  }
  
  /**
   * Add a new photo
   */
  addPhoto(photo) {
    if (this.photoService) {
      this.photoService.addPhoto(photo, false);
    }
    
    // Update folder count
    if (this._state.folder.photos_count !== undefined) {
      this._state.folder.photos_count++;
    }
    
    this.dispatchEvent(new CustomEvent('photoAdded', {
      detail: { photo }
    }));
    
    this.dispatchEvent(new CustomEvent('folderUpdated', {
      detail: { folder: this._state.folder }
    }));
    
    // Start polling if photo is processing
    this.startPollingIfNeeded();
  }
  
  /**
   * Update an existing photo
   */
  updatePhoto(photoId, updates) {
    if (this.photoService) {
      this.photoService.updatePhoto(photoId, updates);
    }
    
    // Event will be forwarded from PhotoService listener
  }
  
  /**
   * Delete a photo
   */
  deletePhoto(photoId) {
    if (this.photoService) {
      this.photoService.removePhoto(photoId);
    }
    
    // Update folder count
    if (this._state.folder.photos_count !== undefined) {
      this._state.folder.photos_count--;
    }
    
    this.dispatchEvent(new CustomEvent('photoDeleted', {
      detail: { photoId }
    }));
    
    this.dispatchEvent(new CustomEvent('folderUpdated', {
      detail: { folder: this._state.folder }
    }));
  }
  
  /**
   * Start upload progress tracking
   */
  startUpload(total) {
    this._state.uploadProgress = { 
      total, 
      uploaded: 0, 
      percentage: 0,
      currentSize: {
        name: '',
        uploaded: 0,
        total: 0,
        percentage: 0
      }
    };
    
    this.dispatchEvent(new CustomEvent('uploadProgress', {
      detail: { 
        uploaded: 0, 
        total, 
        percentage: 0,
        currentSize: this._state.uploadProgress.currentSize
      }
    }));
  }
  
  /**
   * Update upload progress with detailed tracking
   */
  updateUploadProgress(totalUploaded, totalCount, currentSizeName, currentSizeUploaded, currentSizeTotal) {
    if (!this._state.uploadProgress) return;
    
    this._state.uploadProgress.uploaded = totalUploaded;
    this._state.uploadProgress.total = totalCount;
    this._state.uploadProgress.percentage = Math.round((totalUploaded / totalCount) * 100);
    
    this._state.uploadProgress.currentSize = {
      name: currentSizeName,
      uploaded: currentSizeUploaded,
      total: currentSizeTotal,
      percentage: currentSizeTotal > 0 ? Math.round((currentSizeUploaded / currentSizeTotal) * 100) : 0
    };
    
    this.dispatchEvent(new CustomEvent('uploadProgress', {
      detail: {
        uploaded: totalUploaded,
        total: totalCount,
        percentage: this._state.uploadProgress.percentage,
        currentSize: this._state.uploadProgress.currentSize
      }
    }));
  }
  
  /**
   * Increment upload progress
   */
  incrementUploadProgress() {
    if (!this._state.uploadProgress) return;
    
    this._state.uploadProgress.uploaded++;
    const { uploaded, total } = this._state.uploadProgress;
    const percentage = Math.round((uploaded / total) * 100);
    this._state.uploadProgress.percentage = percentage;
    
    this.dispatchEvent(new CustomEvent('uploadProgress', {
      detail: { 
        uploaded, 
        total, 
        percentage,
        currentSize: this._state.uploadProgress.currentSize || { name: '', uploaded: 0, total: 0, percentage: 0 }
      }
    }));
  }
  
  /**
   * Complete upload
   */
  completeUpload() {
    console.log(`[FolderState] Completing upload - hiding progress UI`);
    this._state.uploadProgress = null;
    
    this.dispatchEvent(new CustomEvent('uploadProgress', {
      detail: { uploaded: 0, total: 0, percentage: 100 }
    }));
  }
  
  /**
   * Get upload progress
   */
  getUploadProgress() {
    return this._state.uploadProgress;
  }
  
  /**
   * Check if any photos are processing
   */
  hasProcessingPhotos() {
    if (!this.photoService) return false;
    const photos = this.photoService.getPhotosForFolder(this.getId());
    return photos.some(p => 
      p.processing_status === 'pending' || 
      p.processing_status === 'processing'
    );
  }
  
  /**
   * Start polling for processing status if needed
   */
  startPollingIfNeeded() {
    if (this.hasProcessingPhotos()) {
      this.startPolling();
    } else {
      this.stopPolling();
    }
  }
  
  /**
   * Start polling for processing status updates (delegates to PhotoService)
   */
  startPolling() {
    if (!this.photoService) return;
    this.photoService.startPolling(this.getId());
  }
  
  /**
   * Stop polling (delegates to PhotoService)
   */
  stopPolling() {
    if (!this.photoService) return;
    this.photoService.stopPolling(this.getId());
  }
  
  /**
   * Cleanup and dispose
   */
  dispose() {
    this.stopPolling();
  }
}
